<template>
    <div>
        <div
            v-if="loading"
            class="text-center mt-3"
        >
            {{ $t('app.content_loading') }}
        </div>
        <div v-else>
            <div
                v-for="(period, periodIndex) in formattedPeriods"
                :key="periodIndex"
            >
                <TableAccordionItem
                    :period="getPeriodDate(period)"
                    :balance="currencyFormatter(period.balance)"
                    :is-active="period.active"
                >
                    <div class="block lg:hidden">
                        <div
                            v-for="(item, index) in period.items"
                            :key="index"
                            class="mt-3"
                        >
                            <TableMobileItem>
                                <template #main>
                                    <p>{{ item.chargeName }}</p>
                                    <p>{{ currencyFormatter(item.amount) }}</p>
                                </template>
                                <template #additional>
                                    <p>{{ item.reference }}</p>
                                    <p>{{ dateFormatter(item.dueDate) }}</p>
                                </template>
                            </TableMobileItem>
                        </div>
                    </div>

                    <TableDesktop class="hidden lg:block">
                        <template #colgroup>
                            <col width="20%" />
                            <col width="20%" />
                            <col width="15%" />
                            <col width="25%" />
                            <col width="10%" />
                        </template>
                        <template #headers>
                            <th>{{ $t('payments.list_columns.item_name') }}</th>
                            <th>{{ $t('payments.list_columns.reference_no') }}</th>
                            <th>{{ $t('payments.list_columns.date_posted') }}</th>
                            <th>{{ $t('payments.list_columns.date_due') }}</th>
                            <th>{{ $t('payments.list_columns.amount') }}</th>
                        </template>
                        <template #content>
                            <tr
                                v-for="(item, index) in period.items"
                                :key="index"
                            >
                                <td
                                    :title="item.chargeName"
                                    data-test="chargeName"
                                >
                                    {{ item.chargeName }}
                                </td>
                                <td
                                    :title="item.reference"
                                    data-test="reference"
                                >
                                    {{ item.reference }}
                                </td>
                                <td
                                    :title="dateFormatter(item.postDate)"
                                    data-test="postDate"
                                >
                                    {{ dateFormatter(item.postDate) }}
                                </td>
                                <td
                                    :title="dateFormatter(item.dueDate)"
                                    data-test="dueDate"
                                >
                                    {{ dateFormatter(item.dueDate) }}
                                </td>
                                <td
                                    :title="currencyFormatter(item.amount)"
                                    data-test="amount"
                                >
                                    {{ currencyFormatter(item.amount) }}
                                </td>
                            </tr>
                        </template>
                    </TableDesktop>
                </TableAccordionItem>
            </div>
        </div>
    </div>
</template>

<script>
import TableMobileItem from '@/components/payments/components/TableMobileItem';
import TableDesktop from '@/components/payments/components/TableDesktop';
import TableAccordionItem from '@/components/payments/components/TableAccordionItem';
import { dateFormatter, getPeriodDate, getFirstAndLastDatesMonth } from '@/utils/Date';
import { currencyFormatter } from '@/utils/helpers';
import { defaultMeta } from '@/components/dashboard/constants.js';
import AuthMixin from '@/mixins/AuthMixin';
import NotifyMixin from '@/mixins/NotifyMixin';
import AccountingQueryManager from '@/api/accounting/accountingQueryManager';
import { mapGetters } from 'vuex';
import PayerMixin from '@/mixins/PayerMixin';
import { getAccountingErrorMessage } from '@/utils/accounting';

export default {
    name: 'ChargesList',

    components: {
        TableAccordionItem,
        TableMobileItem,
        TableDesktop,
    },

    mixins: [AuthMixin, NotifyMixin, AccountingQueryManager, PayerMixin],

    props: {
        /**
         * Avaliable totals for certain period
         * with period/balance information
         */
        totals: {
            type: Array,
            required: true,
        },

        /**
         * Selected filter of a period to get
         * accounts receivable data
         */
        selectedPeriod: {
            type: Object,
            required: true,
        },

        /**
         * If the selected option is 'all'
         * then load more balances
         */
        loadMore: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            loading: false,
            periods: [],
        };
    },

    computed: {
        ...mapGetters({
            currentProfile: 'auth/currentProfile',
            chargeTotals: 'accounting/chargeTotals',
        }),
    },

    watch: {
        'currentProfile.occupancyId'(newVal, oldVal) {
            if (newVal && newVal !== oldVal) {
                this.getOccupancy();
                this.getChargesList();
            }
        },

        selectedPeriod(val) {
            if (val) {
                this.getChargesList();
            }
        },
    },

    async created() {
        if (this.currentProfile?.occupancyId) {
            await this.getOccupancy();
            await this.getChargesList();
        }
    },

    methods: {
        dateFormatter,
        getPeriodDate,
        getFirstAndLastDatesMonth,
        currencyFormatter,

        async getChargesList() {
            this.loading = true;
            const { startDate, endDate } = this.selectedPeriod;
            try {
                this.periods = await this.queryHandler('getListCharges', 'resident', {
                    accountId: this.currentProfile.occupancyId,
                    sortField: 'createdAt',
                    startDate,
                    endDate,
                    ...defaultMeta,
                });
            } catch (error) {
                this.notifyError(getAccountingErrorMessage(error));
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>
